import moment from 'moment'
import React from 'react'
import { createStyles, withStyles } from '@mui/styles'
import { Tooltip, Typography } from '@mui/material'
import { LinkOffOutlined, DirectionsBoatOutlined, LocalShippingOutlined } from '@mui/icons-material'
import { t } from '../../../infrastructure/i18nextHelper'
import { ColumnDescriptor, DataTable, DateRangePicker, SapChip } from '../../common/customComponents'
import { muiOptions, defaultStyles, defaultColors } from '../../../infrastructure/materialUiThemeProvider'
import { DealMovementItem } from '../dealModels'
import { DealContainer, dealDialog } from './dealEditStore'
import { popupNavigator } from '../../../infrastructure/popupNavigator'
import { MovementStatus, MovementType, SapFlowStepStatus, MovementTypeNameFromMovementType } from '../../stock/stockModels'
import { MovementStatusIcon } from '../../stock/MovementStatusIcon'
import { MovementTypeIcon, MovementMotIcon } from '../../stock/_movementIcon'
import { ActionDescriptor } from '../../common/components/table/table'
import { steps } from '../../common/sapHelper'
import { UserSettingsContextContainer } from '../../../infrastructure/shell/userSettings/userSettingsStore'
import { hasFeature } from '../../../infrastructure/feature'

function DealMovements({ classes }) {
    let store = DealContainer.useContainer()
    let settings = UserSettingsContextContainer.useContainer()
    let dateFormat = settings.getDateFormat()

    let getTotalFilteredMovementsVolume = () => {
        let currentMovements = store.deal?.movements
        if (currentMovements == null) return
        return currentMovements.reduce((acc, cur) => acc + (cur.volume ?? 0), 0)
    }

    let openMovementVessel = (x: DealMovementItem) => {
        if (x.associatedVesselId)
            popupNavigator.open('vessel', x.associatedVesselId)
        setTimeout(() => dealDialog.close(), 1000)
    }

    let openMovementTruck = (x: DealMovementItem) => {
        if (x.associatedTruckId)
            popupNavigator.open('truck', x.associatedTruckId)
        setTimeout(() => dealDialog.close(), 1000)
    }

    let getMovementTableColumns = () => {
        let columns: ColumnDescriptor<DealMovementItem>[] = [
            { name: t('stock.label.movement.date'), value: x => x.date? moment(x.date?.toString()).format(dateFormat) : null },
            {
                name: t('stock.label.movement.type'), htmlFor: x =>
                    <span className={classes.movementTypeContainer}>
                        {store.deal?.dealType === "BORROWLOAN" &&
                            <span>{MovementTypeNameFromMovementType(x.movementType ?? MovementType.Unknown)}</span>}
                        <MovementTypeIcon movementType={x.movementType ?? MovementType.Unknown} />
                    </span>
            },
            {
                name: t('stock.label.movement.referenceExt'),
                value: x => x.reference,
                wrapText: true,
            },
            {
                name: t('stock.label.originDestination'), htmlFor: x =>
                    <div className={classes.textSubTextContainer}>
                        <div>{x.text}</div>
                        <div className={classes.subText}>{x.subText}</div>
                    </div>
            },
            { name: t('stock.label.movement.mot'), htmlFor: x => <MovementMotIcon meanOfTransportation={x.meanOfTransportation ?? ''} /> },
            {
                name: t('stock.label.movement.volume', { unit: store.unit }),
                textAlign: 'right',
                total: getTotalFilteredMovementsVolume(),
                value: x => x.volume ? Math.round(x.volume).toString() : null,
                totalLabel: t('components.table.total')
            },
            {
                name: t('stock.label.movement.status'), value: x => MovementStatus[x.movementStatus ?? -1],
                htmlFor: x => <MovementStatusIcon movementItem={x} classes={classes} />
            },
            {
                name: `${t('deals.label.pricingStartDate')}`,
                value: x => x.pricingFrom ? moment(x.pricingFrom.toString()).format(dateFormat) : null,
                hidden: !hasFeature('DynamicPricing') || store.deal?.dealType === "BORROWLOAN",
                textAlign: 'right',
            },
            {
                name: `${t('deals.label.pricingEndDate')}`,
                value: x => x.pricingTo ? moment(x.pricingTo.toString()).format(dateFormat) : null,
                hidden: !hasFeature('DynamicPricing') || store.deal?.dealType === "BORROWLOAN",
                textAlign: 'right',
            },
            {
                name: `${t('stock.label.movement.borrowLoanValuation')} (${store.deal?.quantityPriceUnit ?? ''})`,
                value: x => x.borrowLoanValuation ? x.borrowLoanValuation : null,
                hidden: store.deal?.dealType !== "BORROWLOAN",
                textAlign: 'left',
                wrapText: true
            }
        ]

        let sapFlowStep = (x: DealMovementItem, stepStatus: SapFlowStepStatus, index: number) => {
            return <SapChip
                className='default'
                key={index}
                hasValue={stepStatus.isCompleted}
                isFromSap={stepStatus.isFromSap}
                label={stepStatus.isCompleted ? stepStatus.value : stepStatus.name} />
        }

        columns.push(
            {
                name: t('stock.label.movement.sap'), value: x => MovementStatus[x.movementStatus ?? -1],
                htmlFor: x => {
                    let sapFlowSteps = steps(x, true)
                    return (<>
                        <span>{sapFlowSteps.mainSapFlow.map((stepStatus, index) => sapFlowStep(x, stepStatus, index))}</span>
                        {!!sapFlowSteps.secondSapFlow && sapFlowSteps.secondSapFlow.length > 0
                            && <span>-{sapFlowSteps.secondSapFlow.map((stepStatus, index) => sapFlowStep(x, stepStatus, index))}
                            </span>}
                    </>)
                }
            })
        return columns
    }

    let confirmUnassign = (x: DealMovementItem) => {
        store.setDealMovement(x)
        store.setUnAssignConfirmDialogOpen(true)
    }

    let actions: ActionDescriptor<DealMovementItem>[] = [
        {
            name: 'UnAssign', action: (x: DealMovementItem) => confirmUnassign(x),
            icon:
                <Tooltip title={<Typography variant='body1'>{t('deals.label.unAssignDealMovement')}</Typography>} placement='top'>
                    <LinkOffOutlined />
                </Tooltip>,
            isBodyAction: true,
            disabled: _ => store.locked()
        },
        {
            name: 'Vessel', action: (x: DealMovementItem) => openMovementVessel(x),
            icon:
                <Tooltip title={<Typography variant='body1'>{t('deals.label.goToVessel')}</Typography>} placement='top'>
                    <DirectionsBoatOutlined />
                </Tooltip>,
            isBodyAction: true,
            disabled: (x: DealMovementItem) => !x.associatedVesselId
        },
        {
            name: 'truck', action: (x: DealMovementItem) => openMovementTruck(x),
            icon:
                <Tooltip title={<Typography variant='body1'>{t('deals.label.goToTruck')}</Typography>} placement='top'>
                    <LocalShippingOutlined />
                </Tooltip>,
            isBodyAction: true,
            disabled: (x: DealMovementItem) => !x.associatedTruckId
        }
    ]

    let onClickMovement = (dealMovement: DealMovementItem) => {
        popupNavigator.open('movement', dealMovement.movementId)
    }
    return (
        <div>
            {!!store.deal && !!store.deal.movements?.length
                ? <DataTable
                    items={store.deal.movements}
                    isSelectable={false}
                    tableId={'deal-movements-table'}
                    showTotalWhenZero
                    idSelector={(x: DealMovementItem) => x.movementId}
                    onClick={onClickMovement}
                    columns={getMovementTableColumns()}
                    actions={actions} />
                : null
            }
        </div>
    )
}

let styles = theme => createStyles({
    chipRoot: {
        marginLeft: '0.2em',
        marginRight: '0.2em',
        borderRadius: '7px',
    },
    textSubTextContainer: {
        ...defaultStyles.flexColumn,
        alignItems: 'flex-start'
    },
    movementTypeContainer: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem'
    },
    subText: {
        fontSize: '0.85em',
        fontStyle: 'italic',
        color: defaultColors.grey.main.color
    },
})

export default withStyles(styles, muiOptions)(DealMovements)